/** Quick actions on a DETAIL-PAGE value — the prose counterpart of the grids'
 *  `copyableColumn` / `CellActionsPopover`.
 *
 *  Wrap any rendered value (a tag chip, an id, a reference, a timestamp) and
 *  pass the actions it offers: open the record it names, filter a grid by it
 *  in a new tab, copy it, mail/call it, open it on a map. The `act.*` builders
 *  encode the console conventions once (icon, wording, new-tab policy) so a
 *  page never hand-rolls a CellAction:
 *
 *      <ValueActions actions={[act.open(`/tags/${t.id}`, 'Open tag'),
 *                              act.filter(bookingsHref([idCond('tag', t.id)]), 'Bookings with this tag'),
 *                              act.copy(t.name, 'Copy name')]}>
 *        {t.name}
 *      </ValueActions>
 *
 *  Policy (aidocs/plans/2026-09-29-detail-page-links.md, D1/D2): the popover
 *  opens on hover (and click / Enter / Space — the trigger is keyboard
 *  reachable), and every "open" / "filter" / "external" action lands in a NEW
 *  tab so the page being read is never lost. Inline links elsewhere on the
 *  page (LinkField, row clicks) stay same-tab.
 *
 *  Hrefs come from `@/utils/gridLinks` — never build a `?q=` by hand here.
 */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import LaunchIcon from '@mui/icons-material/Launch';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhoneIcon from '@mui/icons-material/Phone';
import PlaceIcon from '@mui/icons-material/Place';
import PublicIcon from '@mui/icons-material/Public';
import { Box, type SxProps, type Theme } from '@mui/material';
import type { ReactNode } from 'react';

import { CellActionsPopover, type CellAction, type TriggerProps } from '@/components/DataGrid/CellActionsPopover';
import { DotSeparated, isShownPart } from '@/components/DotSeparated';
import type { FilterNode } from '@/components/QuerySearch';
import { useSnackbar } from '@/components/Snackbar';
import { aroundCond, TIME_DELTAS } from '@/utils/gridLinks';

/** A copy action is declared by VALUE — the component supplies the clipboard
 *  + snackbar plumbing, so builders stay hook-free. */
export interface CopyActionSpec {
  copy: string;
  label?: string;
}

export type ValueAction = CellAction | CopyActionSpec;

const isCopy = (a: ValueAction): a is CopyActionSpec => 'copy' in a;

const small = { fontSize: 'small' } as const;

/** The conventional actions. Every link opens in a new tab (D1). */
export const act = {
  /** Copy `value`; `label` names it in the tooltip and the snackbar. */
  copy: (value: string, label = 'Copy'): CopyActionSpec => ({ copy: value, label }),

  /** Open a console detail page (new tab unless `sameTab`). */
  open: (to: string, label: string, opts: { sameTab?: boolean } = {}): CellAction => ({
    key: `open:${to}`,
    label,
    icon: <OpenInNewIcon {...small} />,
    href: to,
    newTab: !opts.sameTab,
  }),

  /** Open a grid pre-filtered by this value (new tab). `href` comes from a
   *  gridLinks builder. */
  filter: (href: string, label: string): CellAction => ({
    key: `filter:${href}`,
    label,
    icon: <FilterAltIcon {...small} />,
    href,
    newTab: true,
  }),

  /** Open an external URL (always a new tab). */
  external: (url: string, label: string): CellAction => ({
    key: `ext:${url}`,
    label,
    icon: <LaunchIcon {...small} />,
    href: url,
  }),

  mailto: (email: string): CellAction => ({
    key: `mailto:${email}`,
    label: `Email ${email}`,
    icon: <MailOutlineIcon {...small} />,
    href: `mailto:${email}`,
  }),

  tel: (phone: string): CellAction => ({
    key: `tel:${phone}`,
    label: `Call ${phone}`,
    icon: <PhoneIcon {...small} />,
    href: `tel:${phone}`,
  }),

  /** Google Maps at a coordinate pair or a free-text address. */
  maps: (where: { lat: number; lng: number } | string): CellAction => {
    const query = typeof where === 'string' ? where : `${where.lat},${where.lng}`;
    return {
      key: `maps:${query}`,
      label: 'Open in Google Maps',
      icon: <PlaceIcon {...small} />,
      href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`,
    };
  },

  /** WHOIS lookup for an IP (the audit log's convention). */
  whois: (ip: string): CellAction => ({
    key: `whois:${ip}`,
    label: `WHOIS ${ip}`,
    icon: <PublicIcon {...small} />,
    href: `https://who.is/whois-ip/ip-address/${encodeURIComponent(ip)}`,
  }),

  /** The ±5m … ±7d presets around a timestamp, each linking to the grid
   *  `hrefFor` builds from the range condition (new tab). `ns` is the grid's
   *  datetime namespace (`created`, `updated`, `finalized`, …). */
  timeRanges: (iso: string, hrefFor: (range: FilterNode) => string, ns = 'created'): CellAction[] =>
    TIME_DELTAS.map(({ label, tooltip, ms }) => ({
      key: `${ns}:${label}`,
      label,
      tooltip: `${ns} within ${tooltip} of this time`,
      href: hrefFor(aroundCond(ns, iso, ms)),
      newTab: true,
    })),
};

/** The unambiguous UTC instant, for the popover title of a local-time value. */
export function utcTitle(iso: string): string {
  return `${new Date(iso).toISOString().slice(0, 19).replace('T', ' ')} UTC`;
}

/** A Chip trigger needs the dotted underline switched off — the trigger `sx`
 *  is spread over the base styles, so BOTH rules must be overridden. It is never
 *  wider than its line, so a long chip (a header's category) ellipsizes there
 *  rather than overflowing. */
const CHIP_TRIGGER_SX = {
  textDecoration: 'none',
  '&:hover': { textDecoration: 'none' },
  display: 'inline-flex',
  maxWidth: '100%',
} as const;

interface Props {
  actions: ValueAction[];
  children: ReactNode;
  /** Caption above the actions (e.g. the UTC instant of a timestamp). */
  title?: ReactNode;
  /** Hover opens the popover (default). Click / Enter / Space always do. */
  auto?: boolean;
  /** The child is a Chip — drop the dotted underline. */
  chip?: boolean;
  sx?: SxProps<Theme>;
  /** Extra trigger attributes (`tabIndex`, `data-*`) for a roving-focus host. */
  triggerProps?: TriggerProps;
}

export function ValueActions({ actions, children, title, auto = true, chip = false, sx, triggerProps }: Props) {
  const snackbar = useSnackbar();
  const resolved: CellAction[] = actions.map((a) =>
    isCopy(a)
      ? {
          key: `copy:${a.label ?? ''}:${a.copy}`,
          label: a.label ?? 'Copy',
          icon: <ContentCopyIcon {...small} />,
          onClick: () =>
            void navigator.clipboard.writeText(a.copy).then(
              () => snackbar.show(`Copied "${a.copy}"`, 'success'),
              () => snackbar.show('Copy failed — clipboard unavailable', 'error'),
            ),
        }
      : a,
  );
  if (resolved.length === 0) return <>{children}</>;
  return (
    <CellActionsPopover
      auto={auto}
      actions={resolved}
      title={title}
      sx={chip ? { ...CHIP_TRIGGER_SX, ...sx } : sx}
      triggerProps={triggerProps}
    >
      {children}
    </CellActionsPopover>
  );
}

/** A bare identifier with a copy action — headers, tables, "Row id" fields. */
export function CopyableId({
  value,
  label = 'Copy id',
  mono = true,
}: {
  value: string | number | null | undefined;
  label?: string;
  mono?: boolean;
}) {
  if (value === null || value === undefined || value === '') return <>—</>;
  const text = String(value);
  return (
    <ValueActions actions={[act.copy(text, label)]}>
      <Box component="span" sx={mono ? { fontFamily: 'monospace', fontSize: 13 } : undefined}>
        {text}
      </Box>
    </ValueActions>
  );
}

/** The `tenant · <parent> · ID n` header row. Falsy parts are skipped, so a
 *  page can pass optional pieces without conditionals. `nowrap` (the record headers): each
 *  part stays on one line and the row breaks only between parts, with no dot left at a
 *  line's end or start (`DotSeparated`) — except a part longer than the whole row (a long
 *  email on a phone), which wraps inside itself instead of pushing the page wider. */
export function DetailSubtitle({ parts, nowrap = false }: { parts: ReactNode[]; nowrap?: boolean }) {
  if (nowrap) return <DotSeparated parts={parts} />;
  const shown = parts.filter(isShownPart);
  return (
    <>
      {shown.map((p, i) => (
        // Positional keys are fine: the list is static per render.
        // eslint-disable-next-line react/no-array-index-key
        <Box component="span" key={i}>
          {i > 0 && ' · '}
          {p}
        </Box>
      ))}
    </>
  );
}
